import Tile from 'components/common/recommendation-tiles/tile';
import {
  BlowerDwelling,
  computeHaveSectionViews,
  HaveSection,
  HaveTileRef,
  SelectedTileView
} from 'utils/recommendation-tiles-logic';

type HaveTileCanvasProps = {
  selectedCodes: string[];
  // Persisted virtualFuel literal ('NG'|'LP'|'Oil'|'Electric'|null) — pairs
  // with the fuel-bearing selected code to name its fuel-variant tile.
  fuel: string | null;
  // Structure dwelling mode filtering the Blower offer (Geoff ruling,
  // AHA-6681 dwelling filter). Optional — the pure function owns the
  // 'standard' default (unknown dwelling = standard treatment).
  dwelling?: BlowerDwelling;
  // Fires with the clicked tile's identity (code + fuel variant); the
  // parent resolves radio/deselect semantics via toggleHaveSelection.
  onToggle: (ref: HaveTileRef) => void;
};

const SECTION_LABELS: Record<HaveSection, string> = {
  Outdoor: 'Outdoor units',
  Indoor: 'Indoor units',
  Blower: 'Blower units',
  Packaged: 'All-in-one (packaged)'
};

// The "What you have" sectioned component-tile canvas (AHA-6681 tile panel
// v1.1): four single-select sections with collapse-on-select and symmetric
// all-in-one / AH-blower exclusion. All of that geometry comes from
// computeHaveSectionViews (a pure function of the selection). Only renders
// the interactive virtual canvas — equipment-backed (locked) strips skip
// the canvas entirely and let the ReadoutBar name the system.
export default function HaveTileCanvas({
  selectedCodes,
  fuel,
  dwelling,
  onToggle
}: HaveTileCanvasProps) {
  const views = computeHaveSectionViews(selectedCodes, fuel, dwelling);

  const renderSelected = (view: SelectedTileView) => {
    const ref: HaveTileRef =
      view.kind === 'tile'
        ? { code: view.tile.code, fuel: view.tile.fuel }
        : view.ref;
    const label = view.kind === 'tile' ? view.tile.label : view.label;
    return (
      <Tile
        key={`${ref.code}:${ref.fuel ?? ''}`}
        label={label}
        selected
        tooltip='Selected — click to deselect and see the other options.'
        onClick={() => onToggle(ref)}
      />
    );
  };

  return (
    <div data-testid='have-tile-canvas'>
      <div className='flex flex-wrap items-center gap-2'>
        {views.map((view) => {
          if (view.hidden) return null;
          if (view.selected.length === 0 && view.tiles.length === 0) {
            return null;
          }
          return [
            <span
              key={`header-${view.section}`}
              className='basis-full text-[10.5px] font-bold uppercase tracking-widest text-gray-400 first:mt-0'
            >
              {SECTION_LABELS[view.section]}
            </span>,
            ...view.selected.map(renderSelected),
            ...view.tiles.map((tile) => (
              <Tile
                key={tile.id}
                label={tile.label}
                selected={false}
                onClick={() => onToggle({ code: tile.code, fuel: tile.fuel })}
              />
            ))
          ];
        })}
      </div>
    </div>
  );
}
